import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { Clock, Zap, ChevronDown, Calendar, ArrowUp, ArrowDown, CheckCircle2, AlertCircle, TrendingUp } from 'lucide-react';

// Data constants
const trendData = [
  { month: 'Jan', score: 0 },
  { month: 'Feb', score: 0 },
  { month: 'Mar', score: 0 },
  { month: 'Apr', score: 0 },
  { month: 'May', score: 0 },
  { month: 'Jun', score: 0 }
];

const radarData = [
  { subject: 'Regulatory', A: 0, B: 0 },
  { subject: 'Privacy', A: 0, B: 0 },
  { subject: 'Security', A: 0, B: 0 },
  { subject: 'Incident', A: 0, B: 0 },
  { subject: 'Governance', A: 0, B: 0 },
  { subject: 'Enablement', A: 0, B: 0 }
];

const predictiveInsights = [
  {
    title: "No Insights Available",
    description: "Complete an assessment to generate predictive insights about your AI systems.",
    icon: <Clock className="text-warning" size={20} />,
    status: "warning"
  },
  {
    title: "Assessment Required",
    description: "Run an assessment to understand your AI maturity and trust ratings.",
    icon: <Zap className="text-warning" size={20} />,
    status: "warning"
  },
  {
    title: "Data Collection Needed",
    description: "Connect your systems to enable continuous monitoring and analytics.",
    icon: <TrendingUp className="text-warning" size={20} />,
    status: "warning"
  }
];

function Analytics() {
  const [timeRange, setTimeRange] = useState('6m');
  const [selectedDimensions, setSelectedDimensions] = useState(['Regulatory', 'Privacy', 'Security', 'Incident', 'Governance', 'Enablement']);
  const [peerGroup, setPeerGroup] = useState('Industry');

  const dimensions = ['Regulatory', 'Privacy', 'Security', 'Incident', 'Governance', 'Enablement'];
  const peerGroups = ['Industry', 'Region', 'Custom cohort'];

  return (
    <>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Analytics & Benchmarking</h1>
        <p className="text-text-secondary">Deep‑dive into your AI Trust score trends, compare with peers, and uncover predictive insights.</p>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-2">
          <Calendar size={20} className="text-text-secondary" />
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="bg-background border border-primary/20 rounded-lg px-4 py-2 text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="6m">Last 6 months</option>
            <option value="1y">Last year</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={selectedDimensions[0] || ''}
            onChange={(e) => setSelectedDimensions(e.target.value ? [e.target.value] : [])}
            className="bg-background border border-primary/20 rounded-lg px-4 py-2 text-sm"
          >
            <option value="">All Dimensions</option>
            {dimensions.map(dim => (
              <option key={dim} value={dim}>{dim}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={peerGroup}
            onChange={(e) => setPeerGroup(e.target.value)}
            className="bg-background border border-primary/20 rounded-lg px-4 py-2 text-sm"
          >
            {peerGroups.map(group => (
              <option key={group} value={group}>{group}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div className="bg-background-light rounded-xl border border-primary/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">AI Trust Score</h3>
            <span className="bg-warning/20 text-warning px-3 py-1 rounded-full text-sm">Not Available</span>
          </div>
          <p className="text-2xl font-bold">Awaiting Assessment</p>
        </div>

        <div className="bg-background-light rounded-xl border border-primary/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Industry Rank</h3>
            <span className="bg-warning/20 text-warning px-3 py-1 rounded-full text-sm">Not Ranked</span>
          </div>
          <p className="text-2xl font-bold">No Data</p>
        </div>

        <div className="bg-background-light rounded-xl border border-primary/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Average Enablement Score</h3>
            <span className="bg-warning/20 text-warning px-3 py-1 rounded-full text-sm">0%</span>
          </div>
          <p className="text-2xl font-bold">Not Assessed</p>
        </div>
      </div>

      {/* Trend Bar Chart */}
      <div className="bg-background-light rounded-lg p-6 border border-primary/20 mb-8">
        <h3 className="text-lg font-semibold mb-6">AI Trust Score Over Time</h3>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#5ECF9E" opacity={0.1} />
              <XAxis 
                dataKey="month" 
                stroke="#5ECF9E"
                tick={{ fill: '#5ECF9E' }}
              />
              <YAxis 
                domain={[0, 100]}
                stroke="#5ECF9E"
                tick={{ fill: '#5ECF9E' }}
              />
              <Tooltip
                cursor={{ fill: '#5ECF9E', opacity: 0.15 }}
                contentStyle={{
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  border: '1px solid #5ECF9E',
                  borderRadius: '8px'
                }}
                labelStyle={{ color: '#FFD700' }}
                itemStyle={{ color: '#5ECF9E' }}
              />
              <Bar 
                dataKey="score" 
                fill="#5ECF9E" 
                radius={[4, 4, 0, 0]}
                barSize={30}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Benchmark Radar Chart */}
      <div className="bg-background-light rounded-lg p-6 border border-primary/20 mb-8">
        <h3 className="text-lg font-semibold mb-6">Dimension Benchmark vs. Industry</h3>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="#333" />
              <PolarAngleAxis dataKey="subject" stroke="#fff" />
              <PolarRadiusAxis stroke="#fff" />
              <Radar
                name="Your Company"
                dataKey="A"
                stroke="#5ECF9E"
                fill="#5ECF9E"
                fillOpacity={0.3}
              />
              <Radar
                name="Industry Average"
                dataKey="B"
                stroke="#666"
                fill="#666"
                fillOpacity={0.3}
                strokeDasharray="5 5"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #5ECF9E',
                  borderRadius: '8px',
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Predictive Insights Panel */}
      <div className="bg-background-light rounded-lg p-6 border border-primary/20">
        <h3 className="text-lg font-semibold mb-6">What's Next?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {predictiveInsights.map((insight, index) => (
            <div key={index} className="bg-background rounded-lg p-4 border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                {insight.icon}
                <h4 className="font-semibold">{insight.title}</h4>
              </div>
              <p className="text-text-secondary">{insight.description}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default Analytics; 